import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Diálogo para solicitar recogida o envío al domicilio del cliente.
 * Usado desde la ficha del cliente. POST /customers/{id}/shipment-request.
 *
 * Iter 123: genera una `dedupe_key` (UUID v4) al abrir el diálogo y la
 * envía en la petición. El backend garantiza que peticiones repetidas con
 * la misma clave no generen entradas duplicadas en el historial del cliente.
 * Guarda adicional: `sendingRef` bloquea el doble-click sincrónicamente.
 */
export default function ShipmentDialog({ customer, onClose, onLogged }) {
  const [action, setAction] = useState("pickup");
  const [indications, setIndications] = useState("");
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  // Iter 123 — clave de idempotencia por instancia del diálogo.
  const dedupeKeyRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `dk-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  useEffect(() => () => { sendingRef.current = false; }, []);

  const send = async () => {
    if (sendingRef.current) return; // Iter 123 — guardia sincrónica anti doble-click
    sendingRef.current = true;
    setSending(true);
    try {
      const r = await api.post(`/customers/${customer.id}/shipment-request`, {
        action,
        indications: indications.trim(),
        dedupe_key: dedupeKeyRef.current,
      });
      if (r.data?.deduped) {
        toast.success("Solicitud ya registrada previamente.");
      } else {
        toast.success("Solicitud enviada correctamente y registrada en el historial.");
      }
      if (onLogged) onLogged();
      onClose();
    } catch (err) {
      // Iter 123 — En caso de fallo de email, el backend YA registró la nota
      // como "Error al enviar email de confirmación". Informamos al usuario
      // y refrescamos el timeline igualmente.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_failed") {
        toast.error(`No se pudo enviar el email: ${detail.message || "error desconocido"}. Se ha registrado el intento en el historial.`);
        if (onLogged) onLogged();
        onClose();
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="customer-shipment-dialog"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm">Recogida / Envío</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="border border-[#E5E5E0] p-3 bg-[#F7F7F4] text-xs space-y-0.5">
            <div className="font-semibold text-sm">{customer.name}</div>
            {customer.phone && <div className="font-mono">{customer.phone}</div>}
            {customer.address && <div>{customer.address}</div>}
            {(customer.postal_code || customer.city) && (
              <div>{[customer.postal_code, customer.city].filter(Boolean).join(" · ")}</div>
            )}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1.5">
              ¿Qué quieres gestionar?
            </div>
            <div className="flex gap-2">
              <label
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm cursor-pointer border ${action === "pickup" ? "bg-[#6E8B3D] text-white border-[#6E8B3D]" : "border-[#E5E5E0] hover:border-[#111110]"}`}
                data-testid="shipment-action-pickup"
              >
                <input
                  type="radio"
                  className="sr-only"
                  checked={action === "pickup"}
                  onChange={() => setAction("pickup")}
                />
                Recoger
              </label>
              <label
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm cursor-pointer border ${action === "send" ? "bg-[#6E8B3D] text-white border-[#6E8B3D]" : "border-[#E5E5E0] hover:border-[#111110]"}`}
                data-testid="shipment-action-send"
              >
                <input
                  type="radio"
                  className="sr-only"
                  checked={action === "send"}
                  onChange={() => setAction("send")}
                />
                Enviar
              </label>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              Indicaciones
            </label>
            <textarea
              value={indications}
              onChange={(e) => setIndications(e.target.value)}
              rows={4}
              placeholder="Ej.: Recoger equipo en recepción. Llamar antes de entregar..."
              data-testid="shipment-indications"
              className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="shipment-cancel"
              className="flex-1 border border-[#111110] hover:bg-[#F7F7F4] px-3 py-2 text-sm"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending}
              data-testid="shipment-send"
              className="flex-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {sending ? "Enviando..." : "Enviar solicitud"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
